export const PHYLLOSILICATE_LOGIC = {
  description: "Phyllosilicates (Sheet Silicates) Identification Rules - Micas, Clays, and Serpentine",
  
  coreRules: `
=== 硅酸盐之片状硅酸盐鉴定法则 (Phyllosilicates / Sheet Silicates Protocol) ===

这是地质学中最“柔软”、最具“层理感”的家族。它们的硅氧四面体延伸成二维的无限薄片形网层。鉴定这个家族的核心绝杀技就是：感受它们能不能被剥成极薄的薄片，或者它们的纤维能不能像纸一样被揉捏、像滑石粉一样极其滑腻。

[13.1 撕裂现实的透明书页：云母家族 (The Mica Group)]
云母家族是最好认的矿物，没有之一。
*   **绝杀物理锚点**: 它们拥有**极完全解理 (Perfect basal cleavage)**，这意味着你可以用指甲或小刀，把它们**一层层剥成极薄、具有极高弹性、甚至透明的薄片 (flexible, elastic, extremely thin sheets)**。就像撕开一本书的书页。
*   **白云母 (Muscovite)**: 寻找**无色透明到银白色、淡黄色的极其闪亮的薄片 (colorless to silvery-white, shiny flakes)**。它是绝缘体，常在花岗岩和片岩中闪闪发光。
*   **黑云母 (Biotite)**: 极其致密的**深黑色、黑褐色 (dark black to dark brown)** 闪亮薄片或书页状晶块。
*   **金云母 (Phlogopite)**: 颜色介于两者之间，呈现**黄褐色、金铜色、甚至琥珀色 (yellowish-brown, coppery, amber-like)**。
*   **锂云母 (Lepidolite)**: 锂伟晶岩的标志物。呈现极其娇嫩的**淡紫红色、粉紫色、玫瑰色 (lilac, pink, rose-red)** 的鳞片状集合体。
*   **铬白云母 (Fuchsite)**: 含有铬的白云母变种。寻找在片岩中呈现**极其鲜艳夺目的祖母绿色/翠绿色 (emerald-green, brilliant green)** 小鳞片。

[13.2 变质的绿色油脂：绿泥石与蛇纹石]
这两个家族常常呈现暗沉的绿色，质地偏软，带有滑腻感。
*   **绿泥石 (Chlorite)**:
    *   **形态锚点**: 颜色从**暗绿色到黑绿色 (dark green)**，极常呈现微小的**鳞片状集合体**（像云母一样，但它是暗绿色的而且不能剥成有弹性的薄片，剥下来的薄片一弯就断/没有弹性）。也常在变质岩（绿泥石片岩）中成片出现。
    *   **斜绿泥石 (Clinochlore) / 镁绿泥石 (Penninite)**: 图中展示了极其经典的银绿色/深绿色片状（有时形成蠕虫状晶体或纤维束）。
*   **蛇纹石 (Serpentine)**: 橄榄岩蚀变的终极产物。
    *   **视觉绝杀**: 它的表面通常拥有**如同蛇皮一样的斑驳纹理 (snake-skin mottling)**，颜色从**黄绿色到深绿色交织 (yellow-green to dark olive green)**。
    *   **触觉绝杀**: 它表面常常带有**强烈的滑腻感、油脂光泽或蜡状光泽 (greasy or waxy luster, smooth/soapy feel)**。非常致密块体的是叶蛇纹石 (Antigorite)，而长成极其细长、能像棉线一样被扯出毛状的丝状体的是纤蛇纹石 (Chrysotile / Asbestos 石棉)。

[13.3 极致的滑腻与泥土的芬芳：滑石与黏土矿物]
这是矿物界最软的一群家伙，硬度通常在 1-2 之间。
*   **滑石 (Talc)**: 莫氏硬度 1 的标准矿物。
    *   **核心测试**: 拿出指甲，你可以极其轻松地在上面划出深沟。
    *   **触觉与视觉**: 拥有**终极的滑腻感 (extremely soapy/greasy feel，像肥皂一样)**。颜色多为白色、苹果绿、灰白色。常常长成叶片状或致密块状。
*   **高岭石 (Kaolinite)**: 瓷器的灵魂。寻找**纯白色、灰白色的致密土状块体 (white, earthy, clay-like masses)**。硬度极低，用舌头舔会有强烈的吸水感（粘舌头）。
*   **蒙脱石 (Montmorillonite)**: 膨润土的主要成分。形态上与高岭石类似（灰白/淡黄色土块）。但遇水后会发生极其暴力的**膨胀、变成极其黏滑的糊状 (swells massively in water, becomes highly sticky)**。
*   **绿脱石 (Nontronite)**: 富铁黏土。常常呈现**黄绿色、黄棕色的土状或结核状块体 (yellow-green, earthy)**，外观像风化的锈块或绿泥。

[13.4 其它奇特的片状结晶]
*   **蛭石 (Vermiculite)**: 长得极像黑云母或金云母（黄褐色、黑褐色薄片）。但它的绝杀特性是：如果遇到高温（如火烧），它会**剧烈膨胀、像水蛭一样弯曲伸长 (exfoliates and expands violently into worm-like forms when heated)**。
*   **海泡石 (Sepiolite)**: "海上泡沫"。极其轻盈！如果拿到一块**灰白色、像干枯骨头或硬化黏土 (white, bone-like or earthy)** 的石头，并且发现它**异常轻质甚至能漂浮在水上 (extremely lightweight, porous, floats on water)**，必须是海泡石。
*   **叶蜡石 (Pyrophyllite)**: 长得跟滑石极其相似（白色，极软，有滑腻感）。通常需要通过化学手段或加热反应（与钴盐作用）来区分，但在标本上看到极其致密的白色/淡黄白色腊状石块也需考虑。
*   **硅孔雀石 (Chrysocolla)**: 长得极其像蓝铜矿或孔雀石的朋友。
    *   **视觉锚点**: 寻找**极其亮眼的浅蓝色、蓝绿色、天青色 (cyan-blue, turquoise-blue)**。
    *   **绝杀结构**: 它不会长出像孔雀石那种明显的放射状晶体或完美的钟乳状环带，它永远是**非晶质的、致密的皮壳状、或者像珐琅质/土状的填充物溶结在岩石缝隙里 (amorphous, earthy or enamel-like crusts)**。它还极其黏舌头（因为富含凝胶水）。
*   **葡萄石 (Prehnite)**:虽然结晶学上存在争议（有时被归为其他硅酸盐类），但这里统一进行鉴定。
    *   **视觉绝杀**: 长得像被剥了皮的青提葡萄！寻找在玄武岩空洞中，呈现**极度半透明的淡绿色、油绿色 (pale green to oil-green, translucent)**，且长成**葡萄状、肾状或者球状聚合体 (botryoidal, globular, kidney-like aggregates)**。
*   **鱼眼石 (Apophyllite)**: 也是玄武岩晶洞的常客，常与沸石共生。
    *   **视觉绝杀**: 顶端常常有一个**极度闪亮的平切面（底面解理），反光极强甚至像珍珠/鱼眼白一样的光泽 (pearly luster on basal cleavage faces, highly reflective)**。晶体通常呈现方块状或者带有四方锥形的尖顶。颜色常见的有无色、或者极其诱人的浅绿色。
`
};
